import { useCallback, useMemo } from 'react';
import type { UserPreferences } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useUserPreferencesStoreValue, useUserPreferencesStoreActions } from './context.tsx';
import type { Preferences, Value } from './types.tsx';
import {
	saveUserPreferenceValue,
	removeUserPreferenceValue,
	fetchUserPreferenceValue,
} from './utils.tsx';

export const useUserPreferencesValue = (userPreferenceKey: UserPreferences) => {
	const [value, { setUserPreferenceValue, deleteUserPreferenceValue }] =
		useUserPreferencesStoreValue(userPreferenceKey);

	/**
	 * persist: boolean - if true, save the value to the server ,else just update the local state and return a promise with the new value
	 */
	const setValue = useCallback(
		(newValue: Value, persist = true) => {
			setUserPreferenceValue(userPreferenceKey, newValue);
			if (!persist) {
				return Promise.resolve(newValue);
			}
			return saveUserPreferenceValue(userPreferenceKey, newValue);
		},
		[setUserPreferenceValue, userPreferenceKey],
	);

	const deleteValue = useCallback(() => {
		deleteUserPreferenceValue(userPreferenceKey);
		return removeUserPreferenceValue(userPreferenceKey);
	}, [deleteUserPreferenceValue, userPreferenceKey]);

	// As part of an experiment (jiv-21227_dev_panel_collapsibility_exp) we are introducing collapsibility to the dev panel
	// However, we need to iterate on the backend change that would see the userPreference for our collapsed state of the dev panel
	// come through Relay/Agg.
	// This fetchUserPreferenceValue is a temporary measure to be able to run our initial experiment - if successful, we will remove the below and implement the backend change
	// Please don't use this for anything else except this experiment. Reach out to https://atlassian.enterprise.slack.com/archives/C093S7Q436E
	// Please see the JIRA ticket https://jplat.atlassian.net/browse/JIV-21315 for more details.
	const fetchValue = useCallback(async () => {
		try {
			if (expVal('jiv-21227_dev_panel_collapsibility_exp', 'isEnabled', false)) {
				const serverValue = await fetchUserPreferenceValue(userPreferenceKey);
				if (serverValue !== null) {
					const parsedValue =
						typeof serverValue === 'string' ? JSON.parse(serverValue) : serverValue;
					setUserPreferenceValue(userPreferenceKey, parsedValue);
					return parsedValue;
				}
			}
			return null;
		} catch (error) {
			return null;
		}
	}, [userPreferenceKey, setUserPreferenceValue]);

	const setUserPreferences = useMemo(
		() => ({ setValue, deleteValue, fetchValue }),
		[setValue, deleteValue, fetchValue],
	);

	return [value, setUserPreferences] as const;
};

export const useUserPreferencesActions = () => {
	const [, actions] = useUserPreferencesStoreActions();

	const userPreferenceActions = useMemo(() => {
		const setUserPreferenceValue = (userPreferenceKey: UserPreferences, value: Value) => {
			actions.setUserPreferenceValue(userPreferenceKey, value);
			saveUserPreferenceValue(userPreferenceKey, value);
		};

		const deleteUserPreferenceValue = (userPreferenceKey: UserPreferences) => {
			actions.deleteUserPreferenceValue(userPreferenceKey);
			removeUserPreferenceValue(userPreferenceKey);
		};

		const setUserPreferences = (userPreferences?: Preferences) => {
			userPreferences &&
				Object.keys(userPreferences).length !== 0 &&
				actions.setUserPreferences(userPreferences);
		};

		return {
			setUserPreferenceValue,
			setUserPreferences,
			deleteUserPreferenceValue,
			getUserPreferenceValue: actions.getUserPreferenceValue,
		};
	}, [actions]);

	return [null, userPreferenceActions] as const;
};
